"use client"

import HeadingElement from "@/app/(site)/_components/GCMolecules/HeadingElement"
import ProductDescriptionAccordion from "@/app/(site)/_components/Page/Product/ProductDetail/ProductAccordion/ProductDescriptionAccordion"
import ProductAttributeMapper from "@/app/(site)/_components/Page/Product/ProductDetail/ProductAttributeMapper"
import ProductAttributeTag from "@/app/(site)/_components/Page/Product/ProductDetail/ProductAttributeMapper/ProductAttributeTag"
import ProductDescriptionText from "@/app/(site)/_components/Page/Product/ProductDetail/ProductDescriptionText"
import RelatedProducts from "@/app/(site)/_components/Page/Product/ProductDetail/RelatedProducts"
import { type ProductData } from "@/lib/content-service/types/ContentServiceProduct"
import { t } from "@/translations"
import { clsx } from "@/src/utils/clsx"
import { type CentraLanguageCode } from "@/data/supported-languages"
import { getLangCode } from "@/src/utils/string/getLangCode"
import { usePathname } from "next/navigation"

interface ProductDescriptionProps {
    product: Pick<
        ProductData,
        | "descriptionHtml"
        | "certification"
        | "key_benefits"
        | "weight"
        | "usage_area"
        | "weight_override_title"
        | "usage_area_override_title"
        | "certification_override_title"
        | "key_benefits_override_title"
        | "sku"
        | "relatedProducts"
    >
    langCode: string
}

export default function ProductDescription({
    product,
    langCode
}: ProductDescriptionProps) {
    const { sku, descriptionHtml, relatedProducts } = product

    const locale = usePathname().split("/")[1] ?? ""
    const resolvedLangCode = getLangCode(locale) || (langCode as CentraLanguageCode)

    return (
        <section className="grid h-auto grid-cols-1 bg-col-bg-primary lg:min-h-[328px] lg:grid-cols-[minmax(320px,1fr)_37vw_minmax(320px,1fr)] lg:border-b lg:border-b-col-border xl:grid-cols-[minmax(320px,1fr)_43vw_minmax(320px,1fr)] [@media(min-width:1920px)]:grid-cols-[minmax(320px,1fr)_41vw_minmax(320px,1fr)]">
            <div className="col-start-1 flex flex-col gap-y-32 px-12 py-24 sm:px-16 lg:gap-y-48 lg:border-r lg:border-r-col-border lg:px-24 lg:pb-40 lg:pt-82">
                <ProductAttributeMapper product={product} langCode={langCode} />
                <div className="flex flex-col gap-y-12">
                    <HeadingElement
                        isAboveTheFold={false}
                        text={t(langCode, "product_page.item_number")}
                        type="h2"
                        styleKey="Trim_Mono_POC_Text_14_130_-5_500_uppercase"
                    />
                    <ProductAttributeTag label={sku} />
                </div>
            </div>
            <div className="grid grid-cols-1 gap-x-16 py-40 temp-md:px-16 lg:col-span-2 lg:grid-cols-12 lg:px-24 lg:pb-40 lg:pt-80">
                <div className="hidden temp-md:block lg:col-span-4">
                    {descriptionHtml && (
                        <HeadingElement
                            isAboveTheFold={false}
                            text="Product description"
                            type="h2"
                            styleKey="Trim_Mono_POC_Text_18_130_-3_400_uppercase lg:font_trimMonoPoc_Header_24_130_-4_400_uppercase"
                            className="temp-md:pb-20 lg:pb-0"
                        />
                    )}
                </div>
                {descriptionHtml && (
                    <ProductDescriptionText
                        content={descriptionHtml}
                        className="hidden max-w-[572px] temp-md:block temp-md:pb-40 lg:col-span-8 lg:pb-88"
                    />
                )}

                {/* For mobile only */}
                {descriptionHtml && (
                    <ProductDescriptionAccordion
                        header="Product Description"
                        content={descriptionHtml}
                        className={clsx(
                            `col-start-1 px-12 temp-md:hidden`,
                            relatedProducts.length > 0 && "pb-40"
                        )}
                    />
                )}
                {Array.isArray(relatedProducts) && relatedProducts.length > 0 && (
                    <RelatedProducts
                        products={relatedProducts}
                        langCode={resolvedLangCode}
                        locale={locale}
                    />
                )}
            </div>
        </section>
    )
}
